import type { Controller } from "@ctrl/controller-rpc";

import { PRIMARY_WINDOW_ID } from "@/utils/constants";
import { USER_DATA_LOGS_DIR } from "@/utils/fs";

import {
  DEFAULT_MAX_SERIALIZED_LENGTH,
  DEFAULT_MODULE,
  IDENTIFIERS,
} from "./constants";
import { isLevel } from "./levels";
import type { Logger } from "./renderer";

function nodePath(): typeof import("node:path") {
  return require("node:path");
}

/** `${userData}/logs` — renderer log files (not the controller's State dir). */
export function rendererLogsDir(userDataPath: string): string {
  return nodePath().join(userDataPath, USER_DATA_LOGS_DIR);
}

export function rendererLogFileName(
  component: string,
  sessionId: string
): string {
  return `${component}-${sessionId}.logs`;
}

/**
 * Characters NTFS forbids in file names. The session id is an RFC 3339-style
 * timestamp whose colons Windows rejects — worse, a colon silently switches
 * the path to an NTFS alternate data stream, so writes "succeed" but the file
 * is invisible to `readdirSync` (why Windows Sentry events lack renderer log
 * attachments while `controller.logs.txt` is present).
 */
const WINDOWS_FORBIDDEN_FILE_NAME_CHARS = /[/\\:*?"<>|]/g;

/**
 * Make a session id safe to embed in log file names on the current platform,
 * mirroring the OCaml controller's own sanitization (`log_file_name` in
 * api/controller/lib/platform-unix/impl.ml): on Windows every forbidden
 * character becomes "_"; elsewhere the id is untouched, so macOS/Linux file
 * names keep their historical format. Must be applied where the session id is
 * minted (`logger.electron.initialize`) so every consumer — file writers,
 * collection, deletion — sees the same id.
 */
export function sanitizeSessionIdForFileName(
  sessionId: string,
  platform: NodeJS.Platform = process.platform
): string {
  if (platform !== "win32") return sessionId;
  return sessionId.replace(WINDOWS_FORBIDDEN_FILE_NAME_CHARS, "_");
}

/**
 * Prefix of secondary *window ids* (`secondary-<nanoid>`, see
 * `newSecondaryWindowId` in `src/utils/windows.ts`) — not of file names. In
 * `app-<session>.secondary-<id>.logs` the whole window id sits between the
 * `app-<session>.` stem and the `.logs` extension.
 */
const SECONDARY_WINDOW_ID_PREFIX = "secondary-";

/**
 * File name for a renderer's log. Satellites keep `<identifier>-<session>.logs`.
 * Main windows each write their own file — sharing one would let each window's
 * truncating init (and Clear) wipe the whole session's history — keeping the
 * satellites' `app-<session>` stem so one run's files sort and glob together,
 * with the window id as a dotted suffix:
 *
 *   app-<session>.primary.logs
 *   app-<session>.secondary-<id>.logs
 *
 * A missing window id falls back to the primary's file name (satellites never
 * reach this branch: their identifier is not "app").
 */
export function rendererLogFileNameFor(
  identifier: string,
  sessionId: string,
  windowId: string | null
): string {
  if (identifier !== IDENTIFIERS.APP) {
    return rendererLogFileName(identifier, sessionId);
  }
  const id =
    windowId && windowId.startsWith(SECONDARY_WINDOW_ID_PREFIX)
      ? windowId
      : PRIMARY_WINDOW_ID;
  return `${IDENTIFIERS.APP}-${sessionId}.${id}.logs`;
}

export function rendererLogPathInDir(
  logsDir: string,
  component: string,
  sessionId: string
): string {
  return nodePath().join(logsDir, rendererLogFileName(component, sessionId));
}

/**
 * Window id encoded in a session's app log file name (the inverse of
 * `rendererLogFileNameFor` for the "app" identifier): "primary" for
 * `app-<session>.primary.logs`, `secondary-<id>` for
 * `app-<session>.secondary-<id>.logs`, and null for anything else (other
 * sessions, satellites, non-log files — including a legacy shared
 * `app-<session>.logs`, whose prefix and suffix overlap into an empty id).
 */
export function appLogFileWindowId(
  fileName: string,
  sessionId: string
): string | null {
  const prefix = `${IDENTIFIERS.APP}-${sessionId}.`;
  const suffix = ".logs";
  if (
    !fileName.startsWith(prefix) ||
    !fileName.endsWith(suffix) ||
    fileName.length < prefix.length + suffix.length
  ) {
    return null;
  }
  const windowId = fileName.slice(prefix.length, -suffix.length);
  if (windowId === PRIMARY_WINDOW_ID) return windowId;
  if (
    windowId.startsWith(SECONDARY_WINDOW_ID_PREFIX) &&
    windowId.length > SECONDARY_WINDOW_ID_PREFIX.length
  ) {
    return windowId;
  }
  return null;
}

/**
 * Merge per-window app log texts into one entry stream ordered by timestamp,
 * keeping at most `maxEntriesPerWindow` *parsed* entries per window (counted
 * after parsing, since one entry spans several lines on disk when it carries
 * a stack trace). Secondary windows' entries get a `[win:<id>]` prefix
 * *inside* the message (so `textToEntries` still parses the merged text): on
 * disk the window identity lives in the file name, not on each line.
 */
export function mergeAppLogEntries(
  logs: Array<{ windowId: string; text: string }>,
  maxEntriesPerWindow?: number
): Logger.Entry[] {
  const entries: Logger.Entry[] = [];
  for (const { windowId, text } of logs) {
    let parsed = textToEntries(text);
    if (
      maxEntriesPerWindow !== undefined &&
      parsed.length > maxEntriesPerWindow
    ) {
      parsed = parsed.slice(-maxEntriesPerWindow);
    }
    for (const entry of parsed) {
      entries.push(
        windowId === PRIMARY_WINDOW_ID
          ? entry
          : { ...entry, message: `[win:${windowId}] ${entry.message}` }
      );
    }
  }
  // Array.prototype.sort is stable: entries of one window keep their order.
  return entries.sort((a, b) => a.ts - b.ts);
}

/**
 * Delete every main window's app log for this session ("Clear" in Settings >
 * Logs): the app log is presented merged across windows, so clearing must
 * remove the sibling windows' files too, not just the calling renderer's.
 * Sibling renderers recreate their file on their next append (they open per
 * line), so only pre-clear history is dropped.
 */
export function deleteSessionAppLogs(logsDir: string, sessionId: string): void {
  try {
    const fs: typeof import("node:fs") = require("node:fs");
    for (const name of fs.readdirSync(logsDir)) {
      if (!appLogFileWindowId(name, sessionId)) continue;
      try {
        fs.unlinkSync(nodePath().join(logsDir, name));
      } catch {
        // Another process may hold the file open (Windows); its lines simply
        // survive the clear.
      }
    }
  } catch {
    // Directory unreadable — nothing to clear.
  }
}

/**
 * Ceiling on raw log *lines* read per window file before parsing (roughly
 * 1–2 MB per window): session files grow to tens of MB, and parsing them
 * whole would block the renderer during every Sentry capture. This is only a
 * read/parse bound — callers' entry budgets are applied to *parsed* entries
 * in `mergeAppLogEntries` (an entry spans several lines when it carries a
 * stack trace, so counting lines would under-deliver on error-heavy
 * windows). An entry only drops here if the window's last N *entries* span
 * more than 10 000 lines.
 */
export const MAX_APP_LOG_LINES_PER_WINDOW = 10_000;

/**
 * Last `maxLines` lines of `text` (all of it if shorter), without splitting
 * the whole string: session log files reach tens of MB.
 */
export function tailLines(text: string, maxLines: number): string {
  if (maxLines <= 0) return "";
  // Skip a trailing line terminator so it does not count as an empty line.
  let pos = text.length - (text.endsWith("\n") ? 2 : 1);
  let newlines = 0;
  while (pos >= 0) {
    const nl = text.lastIndexOf("\n", pos);
    if (nl === -1) return text;
    newlines += 1;
    if (newlines === maxLines) return text.slice(nl + 1);
    pos = nl - 1;
  }
  return text;
}

/**
 * Read and merge every main window's app log for this session, keeping at
 * most `maxEntriesPerWindow` parsed entries per window. Each file is first
 * tailed to `MAX_APP_LOG_LINES_PER_WINDOW` raw lines as a read/parse bound (a
 * cut mid multi-line entry drops that entry's orphaned tail: `textToEntries`
 * ignores continuation lines with no header); the entry cap is applied after
 * parsing so stack traces don't eat into the budget. Returns "" when the
 * directory cannot be read or holds no app logs for the session.
 */
export function collectSessionAppLogs(
  logsDir: string,
  sessionId: string,
  maxEntriesPerWindow?: number
): string {
  let names: string[];
  try {
    const fs: typeof import("node:fs") = require("node:fs");
    names = fs.readdirSync(logsDir);
  } catch {
    return "";
  }
  const logs: Array<{ windowId: string; text: string }> = [];
  for (const name of names) {
    const windowId = appLogFileWindowId(name, sessionId);
    if (!windowId) continue;
    const text = readLogFile(nodePath().join(logsDir, name));
    if (text) {
      logs.push({
        windowId,
        text: tailLines(text, MAX_APP_LOG_LINES_PER_WINDOW),
      });
    }
  }
  return entriesToText(mergeAppLogEntries(logs, maxEntriesPerWindow));
}

export function buildRendererLogPath(
  userDataPath: string,
  identifier: string,
  sessionId: string,
  windowId: string | null
): string {
  return nodePath().join(
    rendererLogsDir(userDataPath),
    rendererLogFileNameFor(identifier, sessionId, windowId)
  );
}

/**
 * Controller daemon log file, still under `Platform.logs_dir` (State).
 * Uses the daemon's own filename, not the Electron session id.
 */
export async function buildControllerLogPath(
  controller: Pick<Controller, "logsDirectory" | "logFile">
): Promise<string | null> {
  try {
    const logsDir = await controller.logsDirectory();
    const logFile = await controller.logFile();
    if ("value" in logsDir && "value" in logFile) {
      return nodePath().join(logsDir.value, logFile.value);
    }
  } catch {
    window.console.error("Failed to build the log path for the controller");
  }
  return null;
}

/**
 * Read a log file from disk.
 * Returns undefined if the file does not exist, can't be read, or is empty.
 */
export function readLogFile(logPath: string | null): string | undefined {
  if (!logPath) return;
  try {
    const fs = require("node:fs");
    const data: string = fs.readFileSync(logPath, "utf8");
    if (!data) return;
    return data;
  } catch {
    return;
  }
}

/**
 * Convert timestamp (milliseconds) to "YYYY-MM-DD HH:MM:SS" format
 */
function formatDate(ts: number): string {
  const date = new Date(ts);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const seconds = String(date.getSeconds()).padStart(2, "0");
  return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
}

export type SerializeOptions = {
  maxLen?: number | null;
};

// Hard bounds on the normalization walk. Console args can reference huge object
// graphs (e.g. Vue passes component instances/VNodes to console.warn in dev
// builds, which transitively reach the whole app: parents, appContext,
// provides, the ProseMirror EditorView, ...). Because shared references are
// deliberately re-traversed (see serializeToJsonString's doc), an unbounded
// walk explodes combinatorially and freezes the renderer for good. These caps
// bound the walk regardless of `maxLen`, which only truncates *after* the walk.
const MAX_NORMALIZE_DEPTH = 64;
const MAX_NORMALIZE_NODES = 100_000;

/** Mutable traversal budget shared across one normalization walk. */
type NormalizeBudget = { nodes: number };

function normalizeForJson(
  value: unknown,
  path: WeakSet<object>,
  budget: NormalizeBudget,
  depth: number
): unknown {
  if (value instanceof Error) {
    return { name: value.name, message: value.message, stack: value.stack };
  }

  if (typeof value === "bigint") {
    return String(value);
  }

  if (typeof value !== "object" || value === null) {
    return value;
  }

  // Never walk into DOM nodes, windows, or Vue VNodes: they anchor enormous
  // object graphs and their serialization is useless in logs anyway.
  if (typeof Node !== "undefined" && value instanceof Node) {
    return "[DOMNode]";
  }
  if (typeof Window !== "undefined" && value instanceof Window) {
    return "[Window]";
  }
  if (Reflect.get(value, "__v_isVNode") === true) {
    return "[VNode]";
  }

  if (depth >= MAX_NORMALIZE_DEPTH) {
    return "[MaxDepth]";
  }
  if (budget.nodes <= 0) {
    return "[Truncated]";
  }
  budget.nodes--;

  if (path.has(value)) {
    return "[Circular]";
  }

  path.add(value);
  try {
    const toJSON = Reflect.get(value, "toJSON");
    if (typeof toJSON === "function") {
      const jsonValue = toJSON.call(value);
      if (jsonValue !== value) {
        return normalizeForJson(jsonValue, path, budget, depth + 1);
      }
    }

    if (Array.isArray(value)) {
      return value.map((item) =>
        normalizeForJson(item, path, budget, depth + 1)
      );
    }

    const result: Record<string, unknown> = {};
    for (const key of Object.keys(value)) {
      result[key] = normalizeForJson(
        Reflect.get(value, key),
        path,
        budget,
        depth + 1
      );
    }
    return result;
  } finally {
    path.delete(value);
  }
}

/**
 * Serializes a value to a JSON string, handling circular references, Error objects,
 * and unserializable values. Truncates output if it exceeds maxLen.
 *
 * Circular references are detected only on the current traversal path. Shared
 * references in sibling branches are serialized fully because they are not
 * recursive and are useful in diff-style log payloads.
 */
export function serializeToJsonString(
  value: unknown,
  options: SerializeOptions | number = {}
): string {
  const maxLen =
    typeof options === "number"
      ? options
      : options.maxLen === undefined
        ? DEFAULT_MAX_SERIALIZED_LENGTH
        : options.maxLen;
  try {
    const s = JSON.stringify(
      normalizeForJson(value, new WeakSet(), { nodes: MAX_NORMALIZE_NODES }, 0)
    );
    if (typeof s !== "string") {
      return String(value);
    }
    return maxLen !== null && s.length > maxLen
      ? s.slice(0, maxLen) + "…(truncated)"
      : s;
  } catch {
    try {
      return String(value);
    } catch {
      return "[Unserializable]";
    }
  }
}

/**
 * Parse date string "YYYY-MM-DD HH:MM:SS" to timestamp. The string is local
 * time — `formatDate` writes log lines with local `getHours()` etc. — so parse
 * it as local (no "Z"): appending "Z" would read it as UTC and shift every
 * parse → serialize round-trip (trimming, merging) by the timezone offset.
 */
function parseDateToTimestamp(dateStr: string): number {
  try {
    return new Date(dateStr.replace(" ", "T")).getTime();
  } catch {
    return Date.now();
  }
}

/**
 * Parse log text back to entries.
 * This is the reverse of entriesToText - parses lines in format "DATE [MODULE][LEVEL] MESSAGE"
 */
export function textToEntries(text: string): Logger.Entry[] {
  const entries: Logger.Entry[] = [];
  if (!text || !text.trim()) {
    return entries;
  }

  // Pattern to match: "YYYY-MM-DD HH:MM:SS [MODULE][LEVEL] MESSAGE" (new format with level)
  // Or: "YYYY-MM-DD HH:MM:SS [MODULE] MESSAGE" (old format without level, for backward compatibility)
  // Note: MESSAGE can span multiple lines (e.g., with stack traces)
  const datePatternWithLevel =
    /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[([^\]]+)\]\[([^\]]+)\] (.*)$/;
  const datePatternWithoutLevel =
    /^(\d{4}-\d{2}-\d{2} \d{2}:\d{2}:\d{2}) \[([^\]]+)\] (.*)$/;
  const lines = text.split("\n");

  let currentEntry: Partial<Logger.Entry> | null = null;
  let currentMessage: string[] = [];
  let currentStack: string[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    let match = line.match(datePatternWithLevel);
    let hasLevel = true;

    // Fall back to old format if new format doesn't match
    if (!match) {
      match = line.match(datePatternWithoutLevel);
      hasLevel = false;
    }

    if (match) {
      // Save previous entry if exists
      if (currentEntry) {
        const message = currentMessage.join("\n").trim();
        if (message) {
          entries.push({
            ts: currentEntry.ts || Date.now(),
            level: currentEntry.level || "info",
            module: currentEntry.module || DEFAULT_MODULE,
            message: message,
            args: undefined,
            stack:
              currentStack.length > 0 ? currentStack.join("\n") : undefined,
          });
        }
      }

      // Start new entry
      const dateStr = match[1];
      const module = match[2];
      const level = hasLevel ? match[3] : "info";
      const messageStart = hasLevel ? match[4] : match[3];
      const entryLevel: Logger.Entry["level"] =
        level && isLevel(level) ? level : "info";

      // Parse date to timestamp
      const ts = parseDateToTimestamp(dateStr);

      currentEntry = {
        ts,
        level: entryLevel,
        module: module || DEFAULT_MODULE,
        message: "",
        args: undefined,
        stack: undefined,
      };
      currentMessage = messageStart ? [messageStart] : [];
      currentStack = [];
    } else if (currentEntry) {
      // Continuation of current entry (e.g., stack trace lines)
      if (!line.trim()) continue;

      // `entriesToText()` indents stack traces with 4 spaces; preserve them as `stack` so
      // re-serialization doesn't flatten the stack into the message.
      if (line.startsWith("    ")) {
        currentStack.push(line.slice(4));
      } else {
        currentMessage.push(line);
      }
    }
  }

  // Don't forget the last entry
  if (currentEntry) {
    const message = currentMessage.join("\n").trim();
    if (message) {
      entries.push({
        ts: currentEntry.ts || Date.now(),
        level: currentEntry.level || "info",
        module: currentEntry.module || DEFAULT_MODULE,
        message: message,
        args: undefined,
        stack: currentStack.length > 0 ? currentStack.join("\n") : undefined,
      });
    }
  }

  return entries;
}

/**
 * Convert log entries to text format: DATE [MODULE][LEVEL] MESSAGE
 */
export function entriesToText(entries: Logger.Entry[]): string {
  try {
    if (!entries || !Array.isArray(entries) || entries.length === 0) {
      return "";
    }
    const lines: string[] = [];
    for (const log of entries) {
      try {
        const date = formatDate(log.ts);
        const module = log.module || DEFAULT_MODULE;

        // Try to parse error objects from the message
        let text = log.message;
        let errorInfo: {
          name?: string;
          message?: string;
          stack?: string;
        } | null = null;

        // Normalize newlines in the message (replace with spaces, collapse multiple spaces)
        if (text) {
          text = text.replace(/\n/g, " ").replace(/\s+/g, " ").trim();
        }

        // Check if message is a stringified error object
        if (
          text &&
          text.startsWith("{") &&
          text.includes('"name"') &&
          text.includes('"message"')
        ) {
          try {
            errorInfo = JSON.parse(text);
            if (errorInfo && errorInfo.name && errorInfo.message) {
              text = `${errorInfo.name}: ${errorInfo.message}`;
            }
          } catch {
            // Not valid JSON, use as-is
          }
        }

        // Handle additional arguments (skip if they duplicate the message)
        if (log.args && log.args.length > 0) {
          const messageLower = (log.message || "").toLowerCase().trim();
          const argsText = log.args
            .filter((arg) => {
              // Skip args that are duplicates of the message
              if (typeof arg === "string") {
                const argLower = arg.toLowerCase().trim();
                // Skip if it's the same as message (case-insensitive)
                if (argLower === messageLower) return false;
                // Skip if message is contained in arg or vice versa (to catch partial duplicates)
                if (
                  messageLower &&
                  (argLower.includes(messageLower) ||
                    messageLower.includes(argLower))
                ) {
                  // But allow it if arg contains additional info (like "rpc: {...}")
                  if (argLower.length > messageLower.length * 1.5) return true;
                  return false;
                }
                return true;
              }
              return true;
            })
            .map((arg) => {
              if (typeof arg === "string") {
                // Remove leading/trailing newlines and normalize internal newlines to spaces
                return arg.trim().replace(/\s+/g, " ");
              }
              const converted = serializeToJsonString(arg);
              // Also normalize newlines in converted JSON
              return converted.replace(/\n/g, " ").replace(/\s+/g, " ");
            })
            .join(" ");

          if (argsText) {
            text = text ? `${text} ${argsText}` : argsText;
          }
        }

        // Use stack from errorInfo if available, otherwise use log.stack
        const stack = errorInfo ? errorInfo.stack || log.stack : log.stack;
        if (stack) {
          // Format stack trace with indentation (all lines indented)
          const stackLines = stack.split("\n").map((line) => `    ${line}`);
          text = text
            ? `${text}\n${stackLines.join("\n")}`
            : stackLines.join("\n");
        }

        const line = `${date} [${module}][${log.level}] ${text}\n`;
        lines.push(line);
      } catch (logError) {
        window.console.error(
          "[logger] Error processing individual log entry:",
          logError,
          log
        );
      }
    }
    return lines.join("");
  } catch (error) {
    window.console.error("[logger] Error converting entries to text:", error);
    return "";
  }
}
